"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsGetCampaignDetails = exports.facebookAdsGetCampaignDetailsResultSchema = exports.facebookAdsGetCampaignDetailsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var facebookAdsGetCampaignDetailsInputSchema = zod_1.z
    .object({
    campaignIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Optional array of specific campaign IDs to analyze. If not provided, returns data for all campaigns in the account. e.g. ["123456789", "987654321"]'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for performance data in YYYY-MM-DD format. e.g. 2025-01-01. If omitted, defaults to one year ago.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for performance data in YYYY-MM-DD format. e.g. 2025-01-31. If omitted, defaults to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('The maximum number of campaigns to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    breakdowns: zod_1.z
        .array(zod_1.z.enum([
        'age',
        'gender',
        'country',
        'region',
        'device_platform',
        'publisher_platform',
        'platform_position',
    ]))
        .nullish()
        .describe('Optional breakdowns to segment the data. e.g. ["age", "gender"] for demographic analysis. Note: Using multiple breakdowns or demographic breakdowns (age/gender) will exclude conversion metrics due to Facebook API restrictions.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if no campaign data is found. Unless specified, defaults to true.'),
        groupByDay: zod_1.z
            .boolean()
            .default(false)
            .describe('Group performance data by day. Unless specified, defaults to false.'),
        includeInactive: zod_1.z
            .boolean()
            .default(false)
            .describe('Include inactive campaigns. Unless specified, defaults to false.'),
        includeInsights: zod_1.z
            .boolean()
            .default(false)
            .describe('MUST be explicitly set to true for performance queries. Controls whether detailed insights array is included in response. When true: returns metadata + summary + insights array with granular per-entity metrics (impressions, clicks, spend, CTR, CPC, CPM by campaign/day/segment). When false: returns ONLY metadata + aggregated summary, insights array is completely omitted. Required for ANY performance analysis, comparisons, or detailed data.'),
    })
        .describe('Additional options for the request')
        .default({}),
})
    .describe('Parameters for fetching Facebook Ads campaign details with metadata, creative info, and performance insights. Use for raw data export, workflows, or when user explicitly wants numbers without analysis. For analysis, insights, or recommendations, use facebookAdsInsights instead.');
exports.facebookAdsGetCampaignDetailsArgsSchema = zod_1.z.object({
    args: facebookAdsGetCampaignDetailsInputSchema,
    emitMode: common_1.emitModeSchema,
});
exports.facebookAdsGetCampaignDetailsResultSchema = zod_1.z.object({
    campaigns: zod_1.z
        .array(zod_1.z.object({
        campaign_id: zod_1.z.string(),
        campaign_name: zod_1.z.string(),
        objective: zod_1.z.string().nullish(),
        status: zod_1.z.string().nullish(),
        created_time: zod_1.z.string().nullish(),
        start_time: zod_1.z.string().nullish(),
        stop_time: zod_1.z.string().nullish(),
        budget_remaining: zod_1.z.string().nullish(),
        daily_budget: zod_1.z.string().nullish(),
        lifetime_budget: zod_1.z.string().nullish(),
        insights: zod_1.z
            .array(zod_1.z.object({
            campaign_id: zod_1.z.string().nullish(),
            campaign_name: zod_1.z.string().nullish(),
            date_start: zod_1.z.string().nullish(),
            date_stop: zod_1.z.string().nullish(),
            impressions: zod_1.z.string().nullish(),
            clicks: zod_1.z.string().nullish(),
            spend: zod_1.z.string().nullish(),
            reach: zod_1.z.string().nullish(),
            frequency: zod_1.z.string().nullish(),
            ctr: zod_1.z.string().nullish(),
            cpc: zod_1.z.string().nullish(),
            cpm: zod_1.z.string().nullish(),
            cpp: zod_1.z.string().nullish(),
            conversions: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            cost_per_conversion: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            // Raw Meta verification data
            meta_purchase_roas: zod_1.z
                .array(zod_1.z.object({
                action_type: zod_1.z.string(),
                value: zod_1.z.string(),
            }))
                .nullish(),
            meta_action_values: zod_1.z
                .array(zod_1.z.object({
                action_type: zod_1.z.string(),
                value: zod_1.z.string(),
            }))
                .nullish(),
            meta_actions: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            meta_cost_per_action: zod_1.z
                .union([
                zod_1.z.string(),
                zod_1.z.array(zod_1.z.object({ action_type: zod_1.z.string(), value: zod_1.z.string() })),
            ])
                .nullish(),
            meta_reported_revenue: zod_1.z.number().nullish(),
            meta_reported_roas: zod_1.z.number().nullish(),
            // Breakdown fields (when breakdowns are requested)
            age: zod_1.z.string().nullish(),
            gender: zod_1.z.string().nullish(),
            country: zod_1.z.string().nullish(),
            region: zod_1.z.string().nullish(),
            device_platform: zod_1.z.string().nullish(),
            publisher_platform: zod_1.z.string().nullish(),
            platform_position: zod_1.z.string().nullish(),
        }))
            .nullish(),
    }))
        .describe('List of campaigns with metadata. Each campaign ONLY contains insights array when options.includeInsights: true. When false (default), insights field is completely omitted from each campaign object. Summary statistics are always provided regardless of includeInsights setting.'),
    summary: zod_1.z
        .object({
        total_campaigns: zod_1.z.number(),
        total_spend: zod_1.z.number().nullish(),
        total_impressions: zod_1.z.number().nullish(),
        total_clicks: zod_1.z.number().nullish(),
        total_reach: zod_1.z.number().nullish(),
        total_revenue: zod_1.z.number().nullish(),
        total_conversions: zod_1.z.number().nullish(),
        roas: zod_1.z.number().nullish(),
        meta_roas: zod_1.z.number().nullish(),
        average_cost_per_conversion: zod_1.z.number().nullish(),
        average_ctr: zod_1.z.number().nullish(),
        average_cpc: zod_1.z.number().nullish(),
        average_cpm: zod_1.z.number().nullish(),
        date_range: zod_1.z.object({
            start: zod_1.z.string(),
            end: zod_1.z.string(),
        }),
        top_performing_campaign: zod_1.z
            .object({
            id: zod_1.z.string(),
            name: zod_1.z.string(),
            metric: zod_1.z.string(),
            value: zod_1.z.number(),
        })
            .nullish(),
    })
        .describe('Summary statistics across all campaigns'),
});
exports.facebookAdsGetCampaignDetails = (0, common_1.createEmitModeToolMetadata)({
    id: 'facebookAdsGetCampaignDetails',
    displayName: 'Get Facebook Campaign Details',
    displayDescription: 'Get comprehensive campaign details including metadata, creative info, and performance insights. Always returns campaign metadata and summary statistics. CRITICAL: Granular insights array (per-campaign metrics) is ONLY included when you explicitly set options.includeInsights: true - this is required for ANY performance analysis. Query specific campaigns via campaignIds or omit for all.',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsGetCampaignDetailsArgsSchema,
}, exports.facebookAdsGetCampaignDetailsResultSchema);
